<template>
  <asc-page-loading-indicator
    :loading="isDataLoading && apiPageFailure.length === 0"
  >
    <v-container fluid class="asc-report-list-container">
      <asc-report-list-template
        title="ACMF REPORTS"
        :values="reportListData"
        :export-button-visible="isExporter"
        :export-button-disabled="isLookupDataLoading"
        :on-apu-cycle-export="handleApuCycleExport"
        :on-fault-monitor-export="handleFaultMonitorExport"
        :on-fleet-alerts-export="handleFleetAlertsExport"
        :on-fleet-messages-export="handleFleetMessagesExport"
        :on-export-options="handleExportOptions"
        :on-open="handleReportOpen"
        :on-open-report-summary="handleOpenReportSummary"
        :on-filters-change="handleFiltersChange"
        :on-filters-submit="handleFiltersSubmit"
        :on-filters-disable-submit="handleFiltersDisableSubmit"
        :report-filter-fields="getReportFilterFields"
        :filter-lookups="filterLookups"
        :grid-data-loaded="isGridDataLoaded"
        :event-grid-data-loaded="isEventGridDataLoaded"
        :event-values="eventReportListData"
        :event-report-filter-fields="getEventReportFilterFields"
        :event-report-loading="isEventReportListDataLoading"
        :on-event-report-open="handleEventReportOpen"
        :on-open-event-report-summary="handleOpenEventReportSummary"
        :grid-columns="getEventReportGridColumns"
        grid-filters
        @onEventFiltersChange="handleEventFiltersChange"
      ></asc-report-list-template>

      <asc-report-export-dialog
        :report-types="mappedAcmfReportTypes"
        :airlines="mappedAcmfAirlines"
        :tails="mappedAcmfTails"
        :on-close="handleCloseDialog"
        :on-accept="handleExportOptionsDialog"
        :show-dialog="reportExportDialogVisible"
        :data="reportExportData"
        :pwc-user="isPwcUser"
      ></asc-report-export-dialog>

      <asc-apu-cycle-export-dialog
        :airlines="mappedAcmfAirlines"
        :master-models="mappedAcmfMasterModels"
        :on-close="handleCloseDialog"
        :on-accept="handleApuCycleExportDialog"
        :show-dialog="apuCycleExportDialogVisible"
      ></asc-apu-cycle-export-dialog>

      <asc-fault-monitor-export-dialog
        :airlines="mappedAcmfAirlines"
        :on-close="handleCloseDialog"
        :on-accept="handleFaultMonitorExportDialog"
        :show-dialog="faultMonitorExportDialogVisible"
      ></asc-fault-monitor-export-dialog>

      <asc-fleet-alerts-export-dialog
        :airlines="mappedAcmfAirlines"
        :master-models="mappedAcmfMasterModels"
        :on-close="handleCloseDialog"
        :on-accept="handleFleetAlertsExportDialog"
        :show-dialog="fleetAlertsExportDialogVisible"
      ></asc-fleet-alerts-export-dialog>

      <asc-fleet-messages-export-dialog
        :airlines="mappedAcmfAirlines"
        :ata-chapters="mappedDplfAtaChapters"
        :on-close="handleCloseDialog"
        :on-accept="handleFleetMessagesExportDialog"
        :show-dialog="fleetMessagesExportDialogVisible"
      ></asc-fleet-messages-export-dialog>

      <asc-report-summary-dialog
        :report-data="reportListData"
        :on-close="handleCloseDialog"
        :show-dialog="reportsChartsDialogVisible"
      ></asc-report-summary-dialog>

      <asc-report-summary-dialog
        title="Event Report Summary"
        is-event-report
        :report-data="eventReportListData"
        :on-close="handleCloseDialog"
        :show-dialog="eventReportsChartsDialogVisible"
      ></asc-report-summary-dialog>

      <asc-loading-indicator-dialog
        text="Exporting"
        :loading="true"
        :show-dialog="
          isReportExportLoading ||
          isApuCycleExportLoading ||
          isFaultMonitorExportLoading ||
          isFleetAlertsExportLoading ||
          isFleetMessagesExportLoading
        "
      ></asc-loading-indicator-dialog>
    </v-container>
  </asc-page-loading-indicator>
</template>

<script>
import AscPageLoadingIndicator from '@/components/molecules/AscPageLoadingIndicator'
import AscReportExportDialog from '@/components/molecules/AscReportExportDialog'
import AscApuCycleExportDialog from '@/components/molecules/AscApuCycleExportDialog'
import AscFaultMonitorExportDialog from '@/components/molecules/AscFaultMonitorExportDialog'
import AscFleetMessagesExportDialog from '@/components/molecules/AscFleetMessagesExportDialog'
import AscFleetAlertsExportDialog from '@/components/molecules/AscFleetAlertsExportDialog'
import AscReportSummaryDialog from '@/components/molecules/AscReportSummaryDialog'
import AscLoadingIndicatorDialog from '@/components/molecules/AscLoadingIndicatorDialog'
import { AscReportListTemplate } from '@/components/templates'
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
import {
  ACMF_REPORT_LIST_CONSTS as ARLC,
  GLOBAL_FILTER_CONSTS as GF,
  USER_CONSTS as UC,
  DPLF_CONSTS as DC,
} from '@/stores/consts'
import { AscReportNavMixin } from '../mixins'
import { AscReportListMixin } from '@/components/mixins'
import { getDateGivenOffset } from '@/utilities/dates'
import AppFeatureFlags from '@/utilities/appFeatureFlags'
import { getEventReportGridColumns } from '@/utilities/reports'

export default {
  name: 'AscReportListContainer',

  components: {
    AscPageLoadingIndicator,
    AscReportListTemplate,
    AscReportExportDialog,
    AscApuCycleExportDialog,
    AscFaultMonitorExportDialog,
    AscFleetMessagesExportDialog,
    AscFleetAlertsExportDialog,
    AscReportSummaryDialog,
    AscLoadingIndicatorDialog,
  },

  mixins: [AscReportListMixin, AscReportNavMixin],

  data() {
    return {
      reportFilterFields: [],
      eventReportFilterFields: [],
      isLookupDataLoading: false,
      reportExportDialogVisible: false,
      apuCycleExportDialogVisible: false,
      faultMonitorExportDialogVisible: false,
      fleetMessagesExportDialogVisible: false,
      fleetAlertsExportDialogVisible: false,
      reportsChartsDialogVisible: false,
      eventReportsChartsDialogVisible: false,
      filterLookups: [],
      isGridDataLoaded: false,
      isEventGridDataLoaded: false,
      reportExportData: {},
    }
  },

  computed: {
    ...mapState({ apiPageFailure: 'apiPageFailure' }),
    ...mapState(UC.NAMESPACE, ['airlines']),
    ...mapState(GF.NAMESPACE, ['globalFilter']),
    ...mapState(ARLC.NAMESPACE, [
      'reportListData',
      'isReportListDataLoading',
      'isReportExportLoading',
      'isApuCycleExportLoading',
      'isFaultMonitorExportLoading',
      'isFleetMessagesExportLoading',
      'isFleetAlertsExportLoading',
      'savedReportFilterFields',
      'eventReportListData',
      'isEventReportListDataLoading',
      'savedEventReportFilterFields',
    ]),
    ...mapGetters(GF.NAMESPACE, [
      'airlineIdsSelected',
      'masterModelIdsSelected',
    ]),
    ...mapGetters(DC.NAMESPACE, ['mappedDplfAtaChapters']),
    ...mapGetters(UC.NAMESPACE, [
      'isExporter',
      'isPwcUser',
      'mappedUserAirlines',
    ]),
    ...mapGetters(ARLC.NAMESPACE, [
      'mappedAcmfAirlines',
      'mappedAcmfMasterModels',
      'mappedAcmfTails',
      'mappedAcmfSystems',
      'mappedAcmfReportTypes',
      'mappedAcmfExportMasterModels',
    ]),

    isDataLoading() {
      return (
        this.isEventReportListDataLoading ||
        this.isReportListDataLoading ||
        this.isLookupDataLoading
      )
    },

    getReportFilterFields() {
      return this.reportFilterFields
    },

    getEventReportFilterFields() {
      return this.eventReportFilterFields
    },

    isEventReportActive() {
      return AppFeatureFlags.getEventReports()
    },

    getEventReportGridColumns() {
      return getEventReportGridColumns(this.mappedUserAirlines)
    },
  },

  watch: {
    savedReportFilterFields: {
      deep: true,
      handler() {
        this.initAirlineField()
      },
    },
    globalFilter: {
      deep: true,
      handler() {
        this.handleClearDataGrid()
        this.initFilterLookups1()
        this.initAirlineField()

        this.handleClearEventDataGrid()
        this.resetEventReportFilterFields()
        this.initEventReportFilterFields()
      },
    },
  },

  async created() {
    this.isLookupDataLoading = true

    await Promise.all([
      this.callFilterLookups1(),
      this.callFilterLookups2(),
    ]).then(() => this.initEventReportFilterFields())

    this.isLookupDataLoading = false
  },

  mounted() {
    if (this.savedReportFilterFields.length > 0) {
      this.reportFilterFields = this.savedReportFilterFields
      this.initFilterLookups1()
      this.initAirlineField()
      if (this.savedReportFilterFields.length === 3) {
        const params = this.getFilterParams(this.savedReportFilterFields)
        this.refreshAcmfReportListData(params)
      }
    }

    if (this.savedEventReportFilterFields.length > 0) {
      this.eventReportFilterFields = this.savedEventReportFilterFields
      this.initEventReportFilterFields()
    }
  },

  methods: {
    ...mapActions(ARLC.NAMESPACE, [
      ARLC.GET_ACMF_AIRLINES,
      ARLC.GET_ACMF_MASTER_MODELS,
      ARLC.GET_ACMF_TAILS,
      ARLC.GET_ACMF_SYSTEMS,
      ARLC.GET_ACMF_REPORT_TYPES,
      ARLC.GET_REPORT_LIST_DATA,
      ARLC.GET_REPORT_EXPORT_GIVEN_REPORT_OPTIONS,
      ARLC.GET_APU_CYCLE_EXPORT_GIVEN_OPTIONS,
      ARLC.GET_FAULT_MONITOR_EXPORT_GIVEN_OPTIONS,
      ARLC.GET_FLEET_MESSAGES_EXPORT_GIVEN_OPTIONS,
      ARLC.GET_FLEET_ALERTS_EXPORT_GIVEN_OPTIONS,
      ARLC.GET_EVENT_REPORT_LIST_DATA,
    ]),
    ...mapActions(DC.NAMESPACE, [DC.GET_ATA_CHAPTER_OPTIONS_DATA]),
    ...mapMutations(ARLC.NAMESPACE, [
      ARLC.UPDATE_REPORT_LIST_FILTERS,
      ARLC.UPDATE_REPORT_LIST_DATA,
      ARLC.UPDATE_EVENT_REPORT_LIST_DATA,
      ARLC.UPDATE_EVENT_REPORT_LIST_FILTERS,
    ]),

    async callFilterLookups1() {
      const promise = await Promise.all([
        this[ARLC.GET_ACMF_AIRLINES](),
        this[ARLC.GET_ACMF_MASTER_MODELS](),
      ])

      this.initFilterLookups1()

      this.autoRefreshReportListData()
      return promise
    },

    initFilterLookups1() {
      this.filterLookups = []
      this.loadAirlines()
      this.loadMasterModels()
      this.initReportFilterFields()
      this.isInitialized = false
      this.initAirlineField()
      this.isInitialized = true
    },

    async callFilterLookups2() {
      const promise = await Promise.all([
        this[ARLC.GET_ACMF_TAILS](),
        this[ARLC.GET_ACMF_SYSTEMS](),
        this[ARLC.GET_ACMF_REPORT_TYPES](),
      ])

      if (this.isExporter) {
        await Promise.all([this[DC.GET_ATA_CHAPTER_OPTIONS_DATA]()])
      }

      this.initFilterLookups2()
      return promise
    },

    initFilterLookups2() {
      this.loadTails()
      this.loadSystems()
      this.loadReportTypes()
    },

    async refreshAcmfReportListData(payload) {
      await this[ARLC.UPDATE_REPORT_LIST_FILTERS](this.reportFilterFields)
      await this[ARLC.GET_REPORT_LIST_DATA](payload)
      this.isGridDataLoaded = true
    },

    handleReportOpen(report) {
      const reportId = report.reportId
      const tailNumber = report.tailNumber
      this.navToReportRoute(reportId, tailNumber)
    },

    handleCloseDialog() {
      this.reportExportDialogVisible = false
      this.apuCycleExportDialogVisible = false
      this.faultMonitorExportDialogVisible = false
      this.fleetMessagesExportDialogVisible = false
      this.fleetAlertsExportDialogVisible = false
      this.reportsChartsDialogVisible = false
      this.eventReportsChartsDialogVisible = false
    },

    handleFiltersChange() {
      this.initAirlineField()
      this.handleClearDataGrid()
    },

    handleFiltersSubmit(id, payload) {
      const params = this.getFilterParams(payload)
      this.refreshAcmfReportListData(params)
    },

    autoRefreshReportListDataParams() {
      let aid = 0
      if (this.mappedAcmfAirlines.length === 1) {
        const [airline] = this.mappedAcmfAirlines
        aid = airline.airlineId
      }
      if (this.airlineIdsSelected.length === 1) {
        const [airlineId] = this.airlineIdsSelected
        aid = airlineId
      }
      return [
        {
          id: 'acmfAirline',
          selection: [aid],
        },
        {
          id: 'acmfMasterModel',
          selection: [...this.masterModelIdsSelected],
        },
        {
          id: 'acmfReportDate',
          selection: {
            dates: {
              value: '3',
              startDate: getDateGivenOffset(new Date(), -7),
              endDate: new Date(),
            },
          },
        },
      ]
    },

    async autoRefreshReportListData() {
      const isValidAutoRefresh =
        this.mappedAcmfAirlines.length === 1 ||
        this.airlineIdsSelected.length === 1
      if (isValidAutoRefresh) {
        const reportFilters = this.autoRefreshReportListDataParams()
        const params = this.getFilterParams(reportFilters)
        this.refreshAcmfReportListData(params)
      }
    },

    handleFiltersDisableSubmit() {
      if (this.isLookupDataLoading) {
        return
      }
      this.handleClearDataGrid()
    },

    handleClearDataGrid() {
      this.isGridDataLoaded = false
      this[ARLC.UPDATE_REPORT_LIST_DATA]([])
    },

    handleExportOptions() {
      this.reportExportData = {}
      this.reportExportDialogVisible = true
    },

    handleExportOptionsDialog(payload) {
      this[ARLC.GET_REPORT_EXPORT_GIVEN_REPORT_OPTIONS](payload)
    },

    handleApuCycleExport() {
      this.apuCycleExportDialogVisible = true
    },

    handleApuCycleExportDialog(payload) {
      this[ARLC.GET_APU_CYCLE_EXPORT_GIVEN_OPTIONS](payload)
    },

    handleFaultMonitorExport() {
      this.faultMonitorExportDialogVisible = true
    },

    handleFaultMonitorExportDialog(payload) {
      this[ARLC.GET_FAULT_MONITOR_EXPORT_GIVEN_OPTIONS](payload)
    },

    handleFleetAlertsExport() {
      this.fleetAlertsExportDialogVisible = true
    },

    handleFleetAlertsExportDialog(payload) {
      this[ARLC.GET_FLEET_ALERTS_EXPORT_GIVEN_OPTIONS](payload)
    },

    handleFleetMessagesExport() {
      this.fleetMessagesExportDialogVisible = true
    },

    handleFleetMessagesExportDialog(payload) {
      this[ARLC.GET_FLEET_MESSAGES_EXPORT_GIVEN_OPTIONS](payload)
    },

    handleOpenReportSummary() {
      this.reportsChartsDialogVisible = true
    },

    getFieldSelection(payload, id) {
      const [first] = payload?.filter(item => item.id === id)
      return first || null
    },

    getFilterParams(payload) {
      const reportDate = this.getFieldSelection(payload, 'acmfReportDate')
      const startDate = reportDate ? reportDate.selection.dates.startDate : ''
      const endDate = reportDate ? reportDate.selection.dates.endDate : ''
      const airline = this.getFieldSelection(payload, 'acmfAirline')
      const airlineId = airline ? airline.selection.toString() : ''
      const masterModel = this.getFieldSelection(payload, 'acmfMasterModel')
      const masterModelIds = masterModel ? masterModel.selection.toString() : ''
      const tail = this.getFieldSelection(payload, 'acmfTail')
      const aircraftIds = tail ? tail.selection.toString() : ''
      const system = this.getFieldSelection(payload, 'acmfSystem')
      const systemIds = system ? system.selection.toString() : ''
      const reportType = this.getFieldSelection(payload, 'acmfReportType')
      const reportNumbers = reportType ? reportType.selection.toString() : ''

      const params = {
        startDate,
        endDate,
        airlineId,
        masterModelIds,
        aircraftIds,
        systemIds,
        reportNumbers,
      }

      return params
    },

    getEventReportFilterParams(payload) {
      const reportDate = this.getFieldSelection(payload, 'acmfEventReportDate')
      const startDate = reportDate ? reportDate.selection.dates.startDate : ''
      const endDate = reportDate ? reportDate.selection.dates.endDate : ''

      const params = {
        startDate,
        endDate,
      }

      return params
    },

    loadAirlines() {
      const mappedItem = {
        id: 'acmfAirline',
        items: this.mappedAcmfAirlines,
      }
      this.filterLookups.push(mappedItem)
    },

    loadMasterModels() {
      const mappedItem = {
        id: 'acmfMasterModel',
        items: this.mappedAcmfMasterModels,
      }
      this.filterLookups.push(mappedItem)
    },

    loadTails() {
      const mappedItem = {
        id: 'acmfTail',
        items: this.mappedAcmfTails,
      }
      this.filterLookups.push(mappedItem)
    },

    loadSystems() {
      const mappedItem = {
        id: 'acmfSystem',
        items: this.mappedAcmfSystems,
      }
      this.filterLookups.push(mappedItem)
    },

    loadReportTypes() {
      const mappedItem = {
        id: 'acmfReportType',
        items: this.mappedAcmfReportTypes,
      }
      this.filterLookups.push(mappedItem)
    },

    async handleClearEventDataGrid() {
      this.isEventGridDataLoaded = false
      await this[ARLC.UPDATE_EVENT_REPORT_LIST_DATA]([])
    },

    async resetEventReportFilterFields() {
      await this[ARLC.UPDATE_EVENT_REPORT_LIST_FILTERS]([])
    },

    async refreshAcmfEventReportListData(payload) {
      await this[ARLC.UPDATE_EVENT_REPORT_LIST_FILTERS](
        this.eventReportFilterFields,
      )

      await this[ARLC.GET_EVENT_REPORT_LIST_DATA](payload)
      this.isEventGridDataLoaded = true
    },

    handleEventReportOpen(report) {
      const reportId = report.acmfReportId
      const tailNumber = report.tailNumber
      this.navToReportRoute(reportId, tailNumber)
    },

    handleOpenEventReportSummary() {
      this.eventReportsChartsDialogVisible = true
    },

    autoRefreshEventReportListDataParams() {
      return [
        {
          id: 'acmfEventReportDate',
          selection: {
            dates: {
              value: '3',
              startDate: getDateGivenOffset(new Date(), -7),
              endDate: new Date(),
            },
          },
        },
      ]
    },

    handleEventFiltersChange(payload) {
      this.handleClearEventDataGrid()
      const params = this.getEventReportFilterParams(payload)
      if (params.endDate && params.startDate) {
        this.refreshAcmfEventReportListData(params)
      }
    },
  },
}
</script>

<style lang="scss">
.asc-report-list-container {
  background-color: $gray-100;

  &.container {
    padding: 12px;
  }
}
</style>
